import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Badge, Card } from "reactstrap";

import { ChevronDown } from "react-feather";

import { AlertList, ProgressPage } from "components/Structure";
import { CustomDataTable } from "components/Structure/DataTable/customDataTable";

import {
  handleParamsNote,
  handleSetColumnsProducts,
  handleParamsPage,
} from "redux/taxAnalises";

import { useProductsAbcCurva } from "useCases";

import { getColumns } from "./columns";

import { TableListNotes } from "../Notes";

import DataTableMobile from "components/Structure/DataTable/MobileTable";
import { useArquivaMix } from "hooks";
import { useIsUserMix } from "hooks/useIsUserMix";

export const ListProducts = ({
  data,
  dataRes,
  isLoading,
  paramsSearch,
  revalidate,
}) => {
  const [currentRow, setCurrentRow] = useState(null);

  const {
    columnsProducts,
    tipo,
    params,
    issue_date_start: issueDateStart,
    issue_date_end: issueDateEnd,
  } = useSelector(state => state.taxAnalises);

  const { isArquivaMix } = useArquivaMix();
  const isUserMix = useIsUserMix();

  const dispatch = useDispatch();

  const { data: curvaAbcData } = useProductsAbcCurva({
    issue_date_start: issueDateStart,
    issue_date_end: issueDateEnd,
    tipo_movimento: "SAIDA",
  });

  const cprodsCurvaA = useMemo(
    () =>
      new Set(
        (curvaAbcData?.data ?? []).filter(p => p.abc === "A").map(p => p.cprod),
      ),
    [curvaAbcData],
  );

  const handlePageChange = useCallback(
    page => {
      dispatch(handleParamsNote({ field: "page", value: page }));
    },
    [dispatch],
  );

  const handlePageChangeProduct = useCallback(
    page => {
      dispatch(handleParamsPage({ page }));
    },
    [dispatch],
  );

  const handlePageByInput = page => dispatch(handleParamsPage({ page }));

  const handleChangeFilter = useCallback(
    (field, value) => {
      dispatch(handleParamsNote({ field, value }));
    },
    [dispatch],
  );

  useEffect(() => {
    if (tipo) {
      const columns = getColumns(revalidate, isUserMix, cprodsCurvaA);
      const items = (columns || []).filter(
        item => item?.isType === tipo || !item?.isType,
      );
      dispatch(handleSetColumnsProducts([...items]));
    }
  }, [tipo, dispatch, revalidate, isUserMix, cprodsCurvaA]);

  return (
    <>
      <Card className="p-0 overflow-hidden d-none d-lg-block">
        <CustomDataTable
          className={isArquivaMix && "overflow-hidden"}
          pagination
          sortServer
          paginationServer
          responsive={true}
          fixedHeader
          subHeader
          subHeaderAlign="left"
          subHeaderComponent={
            <div className="w-100 py-1 d-flex flex-nowrap gap-3 justify-content-end">
              <div className="d-flex align-items-start gap-1">
                <span
                  className="bullet bullet-warning"
                  style={{ width: 14, height: 8, marginTop: 6, flexShrink: 0 }}
                />
                <span>
                  <strong>CST Cliente</strong>
                </span>
              </div>
              <div className="d-flex align-items-start gap-1">
                <span
                  className="bullet bullet-info"
                  style={{ width: 14, height: 8, marginTop: 6, flexShrink: 0 }}
                />
                <span>
                  <strong>CST Mix Fiscal</strong>
                </span>
              </div>
              <div className="d-flex align-items-start gap-1">
                <span
                  className="bullet bullet-success"
                  style={{ width: 14, height: 8, marginTop: 6, flexShrink: 0 }}
                />
                <span>
                  <strong>Sem divergência</strong>
                </span>
              </div>
            </div>
          }
          fixedHeaderScrollHeight="70vh"
          progressPending={isLoading}
          columns={columnsProducts}
          data={(data || []).map((item, key) => ({
            ...item,
            key,
          }))}
          sortIcon={<ChevronDown />}
          defaultSortField="invoiceId"
          progressComponent={<ProgressPage />}
          noDataComponent={
            <AlertList title="Sem dados para listagem" description="" />
          }
          page={params?.page}
          totalPages={dataRes?.max_pages}
          paginationClass="justify-content-between align-items-center border-top px-1 py-2 mt-0"
          paginationExtraComponent={
            <Badge
              color="light"
              className="text-secondary fw-medium d-inline-flex align-items-center py-1 px-2 mt-1"
              style={{ fontSize: "0.8rem" }}
            >
              {data?.length || 0} {(data?.length || 0) === 1 ? "item" : "itens"}{" "}
            </Badge>
          }
          expandableRows
          expandableRowExpanded={row => row === currentRow}
          expandOnRowClicked
          onRowClicked={row => setCurrentRow(row)}
          onRowExpandToggled={(bool, row) => {
            setCurrentRow(row);
            dispatch(handleParamsNote({ field: "page", value: 1 }));
          }}
          handlePageChange={handlePageChangeProduct}
          expandableRowsComponent={data => {
            return (
              <TableListNotes
                data={data}
                paramsSearch={paramsSearch}
                handlePageChange={handlePageChange}
                handleChangeFilter={handleChangeFilter}
              />
            );
          }}
          inputPagination
          handleInputPagination={handlePageByInput}
        />
      </Card>
      <DataTableMobile
        columns={columnsProducts}
        data={data || []}
        page={params?.page}
        totalDocs={data?.length}
        totalPages={dataRes?.max_pages}
        isLoading={isLoading}
        expandableRows
        titleAdding="Notas Fiscais"
        childrenExpendade={TableListNotes}
      />
    </>
  );
};
